import * as React from 'react'

import { clsx } from 'clsx'

import { Flex, Paragraph, useControllableState } from '@buffer-mono/popcorn'
import type { ParagraphProps } from '@buffer-mono/popcorn'
import { getFilesFromClipboardItems } from '@buffer-mono/uploader'

import styles from './UploadDropzone.module.css'

interface UploadDropzoneContextType {
  active?: boolean
  disabled: boolean
}
const UploadDropzoneContext =
  React.createContext<UploadDropzoneContextType | null>(null)

function useUploadDropzoneContext(): UploadDropzoneContextType {
  const context = React.useContext(UploadDropzoneContext)

  if (!context) {
    throw new Error(
      'UploadDropzone compound components cannot be rendered outside the UploadDropzone component',
    )
  }

  return context
}

/**
 * UploadDropzone Root
 *
 * [data-state]: open | closed
 * [data-disabled]: true | false
 */

interface UploadDropzoneRootProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onDrop' | 'onPaste'
> {
  className?: string
  /**
   * Whether the dropzone is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * onDrop callback to run when files are successfully dropped
   */
  onDrop?: (files: File[]) => void
  /**
   * onPaste callback to run when files are successfully pasted
   */
  onPaste?: (files: File[]) => void
  /**
   * Whether the alert dialog is open or not
   */
  open?: boolean
  /**
   * Whether dialog is open by default
   */
  defaultOpen?: boolean
  /**
   * Callback to be called when the alert dialog is closed via clicking on
   * any of the actions or pressing the escape key
   */
  onOpenChange?: (open: boolean) => void
}

/**
 * Checks if a drag event is an external file transfer (not an internal drag operation).
 * Returns false for internal drags (e.g., react-dnd reordering) which include 'application/json'.
 */
function isExternalFileTransfer(
  event: React.DragEvent<HTMLDivElement>,
): boolean {
  const types = new Set(event.dataTransfer.types)
  return types.has('Files') && !types.has('application/json')
}

const UploadDropzoneRoot = React.forwardRef<
  HTMLDivElement,
  UploadDropzoneRootProps
>(
  (
    {
      className,
      disabled = false,
      onDrop,
      onPaste,
      children,
      open,
      defaultOpen,
      onOpenChange,
      ...props
    },
    forwardedRef,
  ) => {
    const [active, setActive] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    })

    // Track drag enter/leave balance to handle child element boundary crossing.
    // When dragging over child elements, dragLeave fires on parent before dragEnter
    // fires on child, causing flicker. The counter ensures we only deactivate when
    // truly leaving the dropzone.
    const dragCounterRef = React.useRef(0)

    const handleDragEnter = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (!isExternalFileTransfer(event)) return

        event.preventDefault()
        // Stop propagation to prevent parent handlers from interfering
        event.stopPropagation()
        if (dragCounterRef.current < 0) {
          dragCounterRef.current = 0
        }
        dragCounterRef.current += 1

        if (dragCounterRef.current === 1 && !disabled) {
          setActive(true)
        }
      },
      [disabled, setActive],
    )

    const handleDragOver = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (!isExternalFileTransfer(event)) return

        event.preventDefault()
        // Stop propagation to prevent parent handlers from overriding dropEffect
        // (e.g., ComposerDialog's preventDragAndDrop which sets dropEffect to 'none')
        event.stopPropagation()
        event.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
      },
      [disabled],
    )

    const handleDragLeave = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (!isExternalFileTransfer(event)) return

        event.preventDefault()
        // Stop propagation to prevent parent handlers from interfering
        event.stopPropagation()
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)

        if (dragCounterRef.current === 0 && !disabled) {
          setActive(false)
        }
      },
      [disabled, setActive],
    )

    const handleDrop = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (!isExternalFileTransfer(event)) return

        event.preventDefault()
        event.stopPropagation()

        // Reset drag counter on drop
        dragCounterRef.current = 0
        setActive(false)

        if (disabled) {
          return
        }

        // @TODO: consider using getDroppedFiles approach
        // @see https://github.com/transloadit/uppy/tree/main/packages/%40uppy/utils/src/getDroppedFiles
        const files: File[] = Array.from(event.dataTransfer.files)
        onDrop?.(files)
      },
      [disabled, setActive, onDrop],
    )

    const handlePaste = React.useCallback(
      (event: React.ClipboardEvent<HTMLDivElement>) => {
        // Get the data of clipboard
        const clipboardItems = event.clipboardData?.items
        if (disabled || !clipboardItems) {
          return
        }

        const files = getFilesFromClipboardItems(Array.from(clipboardItems))
        if (files) {
          onPaste?.(files)
        }
      },
      [disabled, onPaste],
    )
    const contextValue = React.useMemo(
      () => ({ active, disabled }),
      [active, disabled],
    )

    return (
      <UploadDropzoneContext.Provider value={contextValue}>
        <div
          ref={forwardedRef}
          className={clsx(styles.wrapper, className)}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPaste={handlePaste}
          data-state={active && !disabled ? 'open' : 'closed'}
          data-disabled={disabled}
          {...props}
        >
          {children}
        </div>
      </UploadDropzoneContext.Provider>
    )
  },
)

UploadDropzoneRoot.displayName = 'UploadDropzone.Root'

/**
 * Overlay for the UploadDropzone
 *
 * [data-state]: open | closed
 * [data-disabled]: true | false
 */

interface UploadDropzoneOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
}

const UploadDropzoneOverlay = React.forwardRef<
  HTMLDivElement,
  UploadDropzoneOverlayProps
>(({ className, children, ...props }, forwardedRef) => {
  const { active, disabled } = useUploadDropzoneContext()

  // When the overlay is visible, it intercepts drag events.
  // We must call preventDefault on dragOver to allow the drop to occur.
  // Only do this when active and for external file transfers.
  const handleDragOver = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!active || disabled || !isExternalFileTransfer(event)) return

      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    [active, disabled],
  )

  return (
    <Flex
      ref={forwardedRef}
      className={clsx(styles.overlay, className)}
      align="center"
      justify="center"
      onDragOver={handleDragOver}
      data-state={active && !disabled ? 'open' : 'closed'}
      data-disabled={disabled}
      {...props}
    >
      <Flex
        className={styles.overlayContentWrapper}
        align="center"
        justify="center"
        direction="column"
      >
        {children}
      </Flex>
    </Flex>
  )
})

UploadDropzoneOverlay.displayName = 'UploadDropzone.Overlay'

/**
 * Content to be displayed in the UploadDropzone's Overlay
 */

interface UploadDropzoneContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
}
const UploadDropzoneContent = React.forwardRef<
  HTMLDivElement,
  UploadDropzoneContentProps
>(({ className, children, ...props }, forwardedRef) => {
  return (
    <Flex
      ref={forwardedRef}
      className={clsx(styles.content, className)}
      align="center"
      justify="center"
      direction="column"
      {...props}
    >
      {children}
    </Flex>
  )
})

UploadDropzoneContent.displayName = 'UploadDropzone.Content'

/**
 * Description for the UploadDropzone's Content
 */

type UploadDropzoneDescriptionProps = ParagraphProps

const UploadDropzoneDescription = React.forwardRef<
  HTMLHeadingElement,
  UploadDropzoneDescriptionProps
>(({ className, ...props }, forwardedRef) => (
  <Paragraph
    ref={forwardedRef}
    className={clsx(styles.description, className)}
    weight="bold"
    size="md"
    {...props}
  />
))

UploadDropzoneDescription.displayName = 'UploadDropzone.Description'

/**
 * Icon for the UploadDropzone's Content
 */

type UploadDropzoneIconProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode
  className?: string
}

const UploadDropzoneIcon = React.forwardRef<
  HTMLImageElement,
  UploadDropzoneIconProps
>(({ className, children }, forwardedRef) => (
  <div className={clsx(styles.icon, className)} ref={forwardedRef} aria-hidden>
    {children}
  </div>
))

UploadDropzoneIcon.displayName = 'OverlayContent.Icon'

export {
  UploadDropzoneRoot as Root,
  UploadDropzoneOverlay as Overlay,
  UploadDropzoneContent as Content,
  UploadDropzoneDescription as Description,
  UploadDropzoneIcon as Icon,
}
export type {
  UploadDropzoneRootProps as RootProps,
  UploadDropzoneOverlayProps as OverlayProps,
  UploadDropzoneContentProps as OverlayContentProps,
}
